/**
 * CountryRiskListsTab.js — Admin-only Settings tab to refresh FATF/OFAC lists.
 *
 * Each FATF plenary (Feb/Jun/Oct), admin pastes the updated ISO-3166 alpha-2
 * codes into the four textareas, hits "Save Lists", and the change is
 * persisted server-side + audit-logged.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Globe, Save, RefreshCw, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/country-risk`;

const KEYS = [
  { id: "FATF_BLACKLIST",     label: "FATF Blacklist",       color: "#ef4444", desc: "Highest risk — call for action (current: IR, KP, MM)" },
  { id: "OFAC_COMPREHENSIVE", label: "OFAC Comprehensive",   color: "#ef4444", desc: "US Treasury OFAC comprehensive sanctions" },
  { id: "FATF_GREYLIST",      label: "FATF Greylist",        color: "#f59e0b", desc: "Increased monitoring — strategic deficiencies" },
  { id: "HIGH_RISK_NON_FATF", label: "High Risk (non-FATF)", color: "#c084fc", desc: "Internal high-risk classification beyond FATF" },
];

function parseCodes(text) {
  return Array.from(new Set(
    text.toUpperCase()
        .split(/[\s,;\n]+/)
        .map((s) => s.trim())
        .filter((s) => /^[A-Z]{2}$/.test(s))
  )).sort();
}

export function CountryRiskListsTab() {
  const [lists, setLists] = useState({});
  const [drafts, setDrafts] = useState({});
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/lists`, { withCredentials: true });
      const fetched = data.lists || {};
      setLists(fetched);
      setMeta(fetched._meta || {});
      const drafts = {};
      KEYS.forEach(k => { drafts[k.id] = (fetched[k.id] || []).join(", "); });
      setDrafts(drafts);
    } catch (e) {
      toast.error("Failed to load country risk lists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { source: "Admin refresh — Rudrik Settings", next_review: "" };
    for (const k of KEYS) {
      const codes = parseCodes(drafts[k.id] || "");
      payload[k.id] = codes;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/refresh`, payload, { withCredentials: true });
      toast.success("Country risk lists updated. Customers will re-score on next view.");
      setLists(data.lists || {});
      setMeta((data.lists || {})._meta || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save lists");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="country-risk-lists-tab" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Globe style={{ width: 20, height: 20, color: "#2563eb" }} />
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Country Risk Lists</h3>
          <span style={{
            background: "rgba(37,99,235,0.15)", border: "1px solid #2563eb55",
            borderRadius: 4, padding: "2px 8px", fontSize: 9, fontWeight: 700,
            color: "#60a5fa", textTransform: "uppercase", letterSpacing: 0.5,
          }}>Admin Only</span>
        </div>
        <button onClick={load} disabled={loading} data-testid="reload-country-lists"
          style={{ background: "transparent", border: "1px solid #1e2530",
                   color: "#94a3b8", borderRadius: 6, padding: "6px 12px",
                   fontSize: 11, fontWeight: 600, cursor: "pointer",
                   display: "inline-flex", alignItems: "center", gap: 6 }}>
          <RefreshCw size={11} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          Reload
        </button>
      </div>

      <p style={{ color: "#94a3b8", fontSize: 12, marginBottom: 16, lineHeight: 1.6 }}>
        Update once per quarter after each FATF plenary (Feb/Jun/Oct). Paste ISO-3166 alpha-2 codes — any
        format (comma/space/newline separated). Invalid codes are filtered out. Changes apply immediately to
        all new screenings; existing customers re-score on next detail view.
      </p>

      {meta.snapshot_date && (
        <div style={{
          background: "rgba(16,185,129,0.08)", border: "1px solid #10b98155",
          borderRadius: 6, padding: "8px 12px", marginBottom: 16,
          fontSize: 11, color: "#34d399",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <CheckCircle size={12} />
          Active snapshot: {meta.snapshot_date} · Source: {meta.source || "—"}
          {meta.updated_by && <> · By: {meta.updated_by}</>}
        </div>
      )}

      <div style={{ display: "grid", gap: 14 }}>
        {KEYS.map(k => {
          const draftCodes = parseCodes(drafts[k.id] || "");
          const liveCodes = lists[k.id] || [];
          const changed = JSON.stringify(draftCodes) !== JSON.stringify([...liveCodes].sort());
          return (
            <div key={k.id} data-testid={`country-list-${k.id.toLowerCase()}`}
              style={{ background: "#0d1117", border: "1px solid #1e2530",
                       borderRadius: 8, padding: 14 }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%", background: k.color,
                  }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: k.color }}>{k.label}</span>
                  <span style={{
                    fontSize: 10, color: "#64748b",
                    background: "#080c12", border: "1px solid #1e2530",
                    borderRadius: 4, padding: "1px 6px", fontFamily: "ui-monospace, monospace",
                  }}>{draftCodes.length} codes</span>
                  {changed && (
                    <span style={{ fontSize: 9, color: "#fbbf24", fontWeight: 700,
                                   textTransform: "uppercase", letterSpacing: 0.5,
                                   background: "rgba(245,158,11,0.12)",
                                   border: "1px solid #f59e0b55",
                                   borderRadius: 3, padding: "1px 6px" }}>
                      unsaved
                    </span>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 10, color: "#64748b", marginBottom: 8 }}>{k.desc}</div>
              <textarea
                value={drafts[k.id] || ""}
                onChange={(e) => setDrafts({ ...drafts, [k.id]: e.target.value })}
                placeholder="IR, KP, MM …"
                data-testid={`country-list-input-${k.id.toLowerCase()}`}
                rows={2}
                style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530",
                         borderRadius: 6, padding: "8px 10px", color: "#f1f5f9",
                         fontSize: 12, fontFamily: "ui-monospace, monospace",
                         letterSpacing: 0.5, resize: "vertical" }}
              />
              {draftCodes.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {draftCodes.map(c => (
                    <span key={c} style={{
                      background: "#080c12", border: `1px solid ${k.color}44`,
                      color: k.color, fontSize: 10, fontWeight: 700,
                      padding: "2px 6px", borderRadius: 4, fontFamily: "ui-monospace, monospace",
                    }}>{c}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={load} disabled={saving} data-testid="discard-country-lists"
          style={{ background: "transparent", border: "1px solid #1e2530",
                   color: "#94a3b8", borderRadius: 6, padding: "10px 16px",
                   fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          Discard
        </button>
        <button onClick={save} disabled={saving} data-testid="save-country-lists"
          style={{
            background: saving ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
            border: "none", color: "white", borderRadius: 6, padding: "10px 18px",
            fontSize: 12, fontWeight: 700,
            cursor: saving ? "not-allowed" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
          {saving ? <RefreshCw size={12} style={{ animation: "spin 1s linear infinite" }} /> : <Save size={12} />}
          {saving ? "Saving…" : "Save Lists"}
        </button>
      </div>

      <div style={{
        marginTop: 14, padding: "8px 12px",
        background: "rgba(148,163,184,0.06)", border: "1px solid #1e2530", borderRadius: 6,
        color: "#64748b", fontSize: 10, display: "flex", alignItems: "center", gap: 6,
      }}>
        <AlertCircle size={11} />
        Reference: <a href="https://www.fatf-gafi.org/en/countries/black-and-grey-lists.html" target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa" }}>FATF Black & Grey Lists</a>
        &nbsp;·&nbsp;<a href="https://ofac.treasury.gov/sanctions-programs-and-country-information" target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa" }}>OFAC Country Sanctions</a>
      </div>
    </div>
  );
}

export default CountryRiskListsTab;
